import secureLocalStorage from "react-secure-storage";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { message as toast } from "antd";
import LoginService from "../../../Services/LoginService";
import DataParser from "../../../utlits/Dataparser";
import "../../../auth-modern.css";

// Small text links under the code field (same look as "Forgot Password?")
const linkButtonStyle = { background: "none", border: "none", padding: 0, fontSize: "13px", color: "#74788d", cursor: "pointer" };

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Two-factor step after the password: { method, maskedEmail, setup, qrCode, manualKey } from the login response
  const [twoFactor, setTwoFactor] = useState(null);
  const [otp, setOtp] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // Shown once after the authenticator app is set up; the login finishes on Continue
  const [backupCodes, setBackupCodes] = useState(null);
  const [pendingLogin, setPendingLogin] = useState(null);

  // 🔹 Load saved credentials and Auto-redirect if already logged in
  useEffect(() => {
    // Park any chat invite token from the URL so it survives the login flow.
    // The backend may generate links pointing to the root (/), so we park here too.
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get("chat_invite");
    if (inviteToken) {
      localStorage.setItem("planit_pending_chat_invite", inviteToken);
      window.history.replaceState({}, "", window.location.pathname);
    }

    // If already authenticated, skip the login page and go straight to the app.
    // The parked invite token above will be consumed by ChatModal on the next route.
    const currentAuth = JSON.parse(secureLocalStorage.getItem("adminInfo"));
    if (currentAuth) {
      const isSaaSAdmin = DataParser.IsSaaSAdmin();
      const company = currentAuth?.company_id;
      const companyRole = DataParser.CompanyIdentifierRole();
      const roleLower = currentAuth?.user?.userdata?.role_title?.toLowerCase();
      const userPermissions = DataParser.CompanyUserPermissions();
      const hasSeenWelcome = currentAuth?.user?.userdata?.has_seen_welcome;

      if (isSaaSAdmin) {
        navigate(`${DataParser.CompanyUrlPrefix()}/saas-admin-dashboard`);
      } else if (company === 0) {
        navigate(`/${companyRole}/admin-dashboard`);
      } else if (roleLower === "hr") {
        navigate(`/${companyRole}/dashboard`);
      } else if (roleLower === "company admin") {
        navigate(`/${companyRole}/employee-attendance`);
      } else if (hasSeenWelcome === true) {
        if (userPermissions.includes("myattendance-list")) {
          navigate(`/${companyRole}/employee-attendance`);
        } else if (userPermissions.includes("tasks-list")) {
          navigate(`/${companyRole}/tasks`);
        } else if (userPermissions.includes("myleave-list")) {
          navigate(`/${companyRole}/employee_leaves`);
        } else if (userPermissions.includes("myevaluation-list")) {
          navigate(`/${companyRole}/myevaluation`);
        } else {
          navigate(`/${companyRole}/employee-attendance`);
        }
      } else {
        navigate("/home");
      }
      return;
    }

    const savedEmail = localStorage.getItem("rememberEmail");
    const savedPassword = localStorage.getItem("rememberPassword");
    const savedRemember = localStorage.getItem("rememberMe");

    if (savedRemember === "true" && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  // "Resend code in Ns" countdown for the emailed code
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const completeLogin = (res) => {
    secureLocalStorage.setItem("adminInfo", JSON.stringify(res));
    secureLocalStorage.setItem("admins", res.authToken);
    secureLocalStorage.setItem("loginTime", JSON.stringify(new Date().getTime()));
    secureLocalStorage.setItem("sessionTimeoutMins", JSON.stringify(res.sessionTimeoutMins || 360));

    if (rememberMe) {
      localStorage.setItem("rememberEmail", email);
      localStorage.setItem("rememberPassword", password);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberEmail");
      localStorage.removeItem("rememberPassword");
      localStorage.removeItem("rememberMe");
    }

    const role = res?.user?.userdata?.role_title || "";
    const roleId = res?.user?.userdata?.role;
    const isSaaSAdmin = res?.user?.userdata?.is_saas_admin === true || ((role?.toLowerCase() === "saas admin" || roleId === 12) && !res.company_id);
    const policyAssigned = res?.user?.company?.policy;
    const userPermissions = res?.user?.userdata?.user_permissions || [];

    if (role?.toLowerCase() !== "company admin" && !isSaaSAdmin && userPermissions.length === 0) {
      setMessage("No permission assigned to you. Please contact company admin or hr to add permissions");
      secureLocalStorage.removeItem("adminInfo");
      secureLocalStorage.removeItem("admins");
      setLoading(false);
      return;
    }

    if (role?.toLowerCase() !== "company admin" && !isSaaSAdmin && !policyAssigned) {
      setMessage("No policy is assigned to your account. Please contact your HR or Administrator.");
      secureLocalStorage.removeItem("adminInfo");
      secureLocalStorage.removeItem("admins");
      setLoading(false);
      return;
    }

    const navigateTo = (path) => {
      // Trigger state refresh in App.js to pick up new login info
      window.dispatchEvent(new Event("permissionsUpdated"));

      // Delay to ensure PrivateComponent re-checks auth after permissionsUpdated fires
      setTimeout(() => {
        navigate(path);
      }, 200);
    };

    const company = res.company_id;
    if (isSaaSAdmin) {
      navigateTo(`${DataParser.CompanyUrlPrefix()}/saas-admin-dashboard`);
    } else if (company === 0) {
      navigateTo(`/${DataParser.CompanyIdentifierRole()}/admin-dashboard`);
    } else {
      const roleLower = role?.toLowerCase();
      const companyRole = res?.user?.company?.company_name || res?.user?.company?.company_identifier || String(res.company_id || "");

      if (roleLower === "hr") {
        navigateTo(`/${companyRole}/dashboard`);
      } else if (roleLower === "company admin") {
        navigateTo(`/${companyRole}/employee-attendance`);
      } else {
        const hasSeenWelcome = res?.user?.userdata?.has_seen_welcome;
        if (hasSeenWelcome === true) {
          if (userPermissions.includes("myattendance-list")) {
            navigateTo(`/${companyRole}/employee-attendance`);
          } else if (userPermissions.includes("tasks-list")) {
            navigateTo(`/${companyRole}/tasks`);
          } else if (userPermissions.includes("myleave-list")) {
            navigateTo(`/${companyRole}/employee_leaves`);
          } else if (userPermissions.includes("myevaluation-list")) {
            navigateTo(`/${companyRole}/myevaluation`);
          } else {
            navigateTo(`/${companyRole}/employee-attendance`);
          }
        } else {
          navigateTo("/home");
        }
      }
    }
  };

  // twoFactorFields: { otp } or { backup_code } on the two-factor step; none sends (or resends) the code
  const submitLogin = (twoFactorFields = {}) => {
    setLoading(true);
    setMessage("");

    const loginData = { email, password, ...twoFactorFields };

    LoginService.loginAdmin(loginData)
      .then((res) => {
        if (!res.Good) {
          setMessage(res.data);
          setLoading(false);
          return;
        }

        if (res.isDefaultPassword) {
          // the default password just typed proves it's this person setting the new one (kept in memory only)
          navigate("/initial-reset-password", { state: { email: res.userEmail, currentPassword: password } });
          setLoading(false);
          return;
        }

        // Password accepted; the two-factor code is needed next
        if (res.requires2FA) {
          setTwoFactor(res);
          if (res.resendIn) setResendIn(res.resendIn);
          setLoading(false);
          return;
        }

        if (res.twoFactorBackupCodes) {
          setBackupCodes(res.twoFactorBackupCodes);
          setPendingLogin(res);
          setLoading(false);
          return;
        }

        completeLogin(res);
      })
      .catch((err) => {
        setMessage(err.response?.data?.message || err.message);
        setLoading(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!twoFactor) {
      submitLogin();
      return;
    }
    submitLogin(useBackupCode ? { backup_code: otp } : { otp });
  };

  const resendCode = () => {
    setOtp("");
    submitLogin();
  };

  const backToLogin = () => {
    setTwoFactor(null);
    setOtp("");
    setUseBackupCode(false);
    setResendIn(0);
    setMessage("");
    setLoading(false);
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"))
      .then(() => toast.success("Backup codes copied"))
      .catch(() => toast.error("Could not copy. Please write the codes down."));
  };

  const downloadBackupCodes = () => {
    const text = `Planit backup codes for ${email}\nEach code works once.\n\n${backupCodes.join("\n")}\n`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    link.download = "planit-backup-codes.txt";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const finishLogin = () => {
    setLoading(true);
    completeLogin(pendingLogin);
  };

  const heading = backupCodes
    ? "Save your backup codes"
    : twoFactor?.setup
      ? "Set up your authenticator app"
      : twoFactor
        ? "Two-factor verification"
        : "Welcome back!";

  const twoFactorHint = !twoFactor || twoFactor.setup
    ? ""
    : twoFactor.method === "email"
      ? `Enter the 6-digit code sent to ${twoFactor.maskedEmail}.`
      : useBackupCode
        ? "Enter one of the backup codes you saved when you set up the app."
        : "Enter the 6-digit code from your authenticator app.";

  return (
    <>
      <Helmet>
        <title>Login - Planit</title>
      </Helmet>

      <div className={`auth-modern-wrapper${twoFactor || backupCodes ? " auth-wrapper-tall" : ""}`}>
        <div className="auth-modern-card">
          <div className="auth-logo-container">
            <Link to="/">
              <img src="/assets/images/logo-dark.png" alt="logo" height={32} />
            </Link>
          </div>

          <h2 className="auth-welcome-text">{heading}</h2>

          {message && <div className="auth-error-alert">{message}</div>}

          {backupCodes ? (
            <div>
              <p className="text-center mb-3" style={{ fontSize: "13px", color: "#74788d" }}>
                Your authenticator app is set up. If you lose your phone, each of these codes lets you log in once. Save them somewhere safe; they won't be shown again.
              </p>
              <div className="d-flex flex-wrap justify-content-between mb-3" style={{ rowGap: "8px" }}>
                {backupCodes.map((code) => (
                  <code key={code} style={{ width: "48%", textAlign: "center", padding: "8px", background: "#f5f6f8", borderRadius: "6px", fontSize: "14px", color: "#1D1D1D" }}>{code}</code>
                ))}
              </div>
              <div className="d-flex justify-content-center mb-4" style={{ gap: "24px" }}>
                <button type="button" style={linkButtonStyle} onClick={copyBackupCodes}>Copy</button>
                <button type="button" style={linkButtonStyle} onClick={downloadBackupCodes}>Download</button>
              </div>
              {!loading ? (
                <button className="auth-primary-btn" type="button" onClick={finishLogin}>I've saved them, continue</button>
              ) : (
                <button className="auth-primary-btn" disabled>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Logging In...
                </button>
              )}
            </div>
          ) : twoFactor ? (
            <form onSubmit={handleSubmit}>
              {twoFactor.setup ? (
                <div className="mb-3" style={{ fontSize: "13px", color: "#74788d" }}>
                  <p className="text-center mb-2">1. Scan this QR code with Google Authenticator, Microsoft Authenticator or Authy on your phone.</p>
                  <div className="d-flex align-items-center justify-content-center" style={{ gap: "16px" }}>
                    <img src={twoFactor.qrCode} alt="QR code for your authenticator app" width={150} height={150} style={{ flexShrink: 0 }} />
                    <div>
                      <p className="mb-1">Can't scan it? Enter this key in the app:</p>
                      <code style={{ display: "block", fontSize: "13px", letterSpacing: "1px", lineHeight: 1.6, color: "#1D1D1D" }}>{twoFactor.manualKey}</code>
                    </div>
                  </div>
                  <p className="text-center mt-2 mb-0">2. Enter the 6-digit code the app shows.</p>
                </div>
              ) : (
                <p className="text-center mb-3" style={{ fontSize: "13px", color: "#74788d" }}>{twoFactorHint}</p>
              )}

              <div className="auth-input-group">
                <input
                  type="text"
                  className="auth-input"
                  placeholder=" "
                  id="twofactorcode"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  autoComplete="one-time-code"
                  inputMode={useBackupCode ? "text" : "numeric"}
                  maxLength={useBackupCode ? 11 : 6}
                  autoFocus
                  required
                />
                <label className="auth-label" htmlFor="twofactorcode">{useBackupCode ? "Backup code" : "6-digit code"}</label>
              </div>

              {(twoFactor.method === "email" || !twoFactor.setup) && (
                <div className="d-flex justify-content-end align-items-center mb-3">
                  {twoFactor.method === "email" && (
                    resendIn > 0
                      ? <span style={{ fontSize: "13px", color: "#74788d" }}>Resend code in {resendIn}s</span>
                      : <button type="button" style={linkButtonStyle} onClick={resendCode} disabled={loading}>Resend code</button>
                  )}
                  {twoFactor.method === "app" && !twoFactor.setup && (
                    <button type="button" style={linkButtonStyle} onClick={() => { setUseBackupCode(!useBackupCode); setOtp(""); setMessage(""); }}>
                      {useBackupCode ? "Use authenticator code" : "Use a backup code"}
                    </button>
                  )}
                </div>
              )}

              {!loading ? (
                <button className="auth-primary-btn" type="submit">Verify</button>
              ) : (
                <button className="auth-primary-btn" disabled>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Verifying...
                </button>
              )}

              <div className="text-center mt-3">
                <button type="button" style={{ ...linkButtonStyle, fontWeight: 600 }} onClick={backToLogin}>Back to login</button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="auth-input-group">
              <input
                type="email"
                className="auth-input"
                placeholder=" "
                id="useremail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label className="auth-label" htmlFor="useremail">Email</label>
            </div>

            <div className="auth-input-group">
              <input
                type={showPassword ? "text" : "password"}
                className="auth-input"
                placeholder=" "
                id="userpassword"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <label className="auth-label" htmlFor="userpassword">Password</label>
              <button
                type="button"
                className={`auth-password-toggle${password ? ' auth-password-filled' : ''}`}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <div className="auth-password-icon-container">
                  <img src="/assets/images/password.svg" alt="" />
                  {!showPassword && <div className="auth-password-slash"></div>}
                </div>
              </button>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="auth-remember-check"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="auth-remember-check" style={{ fontSize: '13px' }}>
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password" title="Forgot Password" style={{ fontSize: '13px', color: '#74788d', textDecoration: 'none' }}>
                Forgot Password?
              </Link>
            </div>

            {!loading ? (
              <button className="auth-primary-btn" type="submit">Log In</button>
            ) : (
              <button className="auth-primary-btn" disabled>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Logging In...
              </button>
            )}
          </form>
          )}

          <p className="auth-footer-text">
            © {new Date().getFullYear()} Planit. Developed by <a href="https://websouls.com/" target="_blank" rel="noopener noreferrer" className="auth-link">WebSouls</a>
          </p>

        </div>

        <div className="auth-bottom-help">
          <Link to="/">Need help?</Link>
        </div>
      </div>
    </>
  );
};

export default Login;
